import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, IconButton } from "@material-ui/core";
import { TableContainer } from "modus/components/list/table/TableContainer";
import { GridReadyEvent } from "ag-grid-community";
import ApplicationIcon from "legacy-components/icons/ApplicationIcon";
import ServiceApplicationIcon from "legacy-components/icons/ServiceApplicationIcon";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { useDialogProps } from "legacy-components/dialog/Dialog";
import { ApplicationThrottlingDialog } from "./ApplicationThrottlingDialog";
import DataRemover from "modus/components/misc/DataRemover";
import ApplicationService from "pages/home/applications/components/v2.0/ApplicationService";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";

const Type = ({ value }: { value: any }) => {
  return (
    <Box
      key={value}
      style={{
        height: "100%",
        display: "flex",
        justifyItems: "center",
        alignItems: "center",
      }}
    >
      {value?.name === "application" ? (
        <ApplicationIcon style={{ color: "#0E416C" }} />
      ) : (
        <ServiceApplicationIcon style={{ color: "#0E416C" }} />
      )}
    </Box>
  );
};

interface IApplicationThrottling {
  consumerKey: string;
  applicationName: string;
  applicationType: string;
  teamName: string;
  accountName: string;
  spikeArrestLimit: number;
  spikeArrestTimeunit: "ps" | "pm" | string;
}

interface AppListViewProps {
  deploymentId: string;
  appThrottlingList?: IApplicationThrottling[] | null;
  refresh?: () => void;
  deployStatus?: string;
  useApplicationThrottlingV2?: boolean;
}

const ActionsRenderer = (props: any) => {
  const { data, handleEdit, deployStatus, refresh, deploymentId, useApplicationThrottlingV2 } = props;
  const [deleteState, { subscribe }] = useAsyncAction(() =>
    useApplicationThrottlingV2
      ? ApplicationService.deleteApplicationThrottlingV2(deploymentId, data.consumerKey)
      : ApplicationService.deleteApplicationThrottling(
          deploymentId,
          data.consumerKey,
        ),
  );

  return (
    <Box style={{ height: "100%", display: "flex", alignItems: "center" }} className="app-discovery-page">
      <IconButton
        onClick={() => handleEdit(data)}
        disabled={deployStatus !== "DEPLOY_COMPLETED"}
        size="small"
        style={{ marginRight: 8}}
      >
        <EditOutlinedIcon />
      </IconButton>
      <DataRemover
        style={{ backgroundColor: "#DA212C", color: "#FFFFFF", border: "none" }}
        skipConfirmationDialog={true}
        asyncActionState={deleteState}
        onConfirm={subscribe}
        onSuccess={() => refresh?.()}
        disabled={deployStatus !== "DEPLOY_COMPLETED" || deleteState?.loading}
        enterButtonText={undefined}
        icon={undefined}
        successMessage={`Application Throttling removed for ${data.consumerKey}`}
      ></DataRemover>
    </Box>
  );
};

export const ApplicationThrottlingListView: React.FC<AppListViewProps> =
  React.memo(({ refresh, appThrottlingList, deploymentId, deployStatus, useApplicationThrottlingV2 }) => {
    const containerRef = useRef<HTMLElement>(null);
    const { open, toggle } = useDialogProps();
    const [selectedThrottlingItem, setSelectedThrottlingItem] =
      useState<IApplicationThrottling | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
      params.api.sizeColumnsToFit();
    };

    const handleEdit = useCallback(
      (data: IApplicationThrottling) => {
        setSelectedThrottlingItem(data);
        toggle();
      },
      [toggle],
    );

    const editModeMap = useMemo(() => {
      if (selectedThrottlingItem) {
        return {
          [selectedThrottlingItem.consumerKey]:
            selectedThrottlingItem.applicationName,
        };
      }
      return {};
    }, [selectedThrottlingItem]);
    return (
      <Box>
        {appThrottlingList && appThrottlingList.length > 0 && (
          <TableContainer ref={containerRef}>
            <AgGridReact
              rowData={appThrottlingList}
              frameworkComponents={{ Type, ActionsRenderer }}
              rowHeight={48}
              headerHeight={48}
              domLayout={"autoHeight"}
              onGridReady={onGridReady}
              columnDefs={[
                {
                  headerName: "Type",
                  field: "applicationType",
                  cellRenderer: "Type",
                  flex: 1,
                  width: 88,
                  valueGetter: (params) => ({ name: params.data.applicationType }),

                  // maxWidth: 70,
                },
                {
                  headerName: "Application Name",
                  field: "applicationName",
                  cellRenderer: "AppTitleLink",
                  flex: 2,
                },
                {
                  headerName: "Team Name",
                  field: "teamName",
                  flex: 2,
                },
                {
                  headerName: "Account",
                  field: "accountName",
                  flex: 2,
                },
                {
                  headerName: "Rate Limiting",
                  valueGetter: (params) => {
                    const limit = params.data.spikeArrestLimit;
                    const unitRaw = params.data.spikeArrestTimeunit;

                    let unitLabel = "Second";
                    if (unitRaw === "pm" || unitRaw === "MINUTE") {
                      unitLabel = "Minute";
                    } else if (unitRaw === "ps" || unitRaw === "SECOND") {
                      unitLabel = "Second";
                    }

                    return `${limit} Transactions / ${unitLabel}`;
                  },
                  flex: 3,
                },
                {
                  headerName: "Actions",
                  field: "consumerkey",
                  cellRenderer: "ActionsRenderer",
                  cellRendererParams: {
                    handleEdit: handleEdit,
                    deployStatus: deployStatus,
                    refresh: refresh,
                    deploymentId: deploymentId,
                    useApplicationThrottlingV2: useApplicationThrottlingV2,
                  },
                  flex: 1,
                  sortable: false,
                  filter: false,
                  suppressSizeToFit: true,
                },
              ]}
            />
          </TableContainer>
        )}
        {open && (
          <ApplicationThrottlingDialog
            deploymentId={deploymentId}
            open={open}
            toggle={() => {
              toggle();
              if (open) setSelectedThrottlingItem(null);
            }}
            consumerKeyAppNameMap={editModeMap}
            refresh={refresh}
            buttonType="Update"
            initilalValues={selectedThrottlingItem}
            useApplicationThrottlingV2={useApplicationThrottlingV2}
          />
        )}
      </Box>
    );
  });
